/**
 * Erros da API seguem a RFC 9457 (application/problem+json).
 * Ver apps/api/app/Support/Http/Problems/ProblemRenderer.php.
 */
export interface ProblemDetails {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  request_id?: string
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.problem = problem
  }

  get status(): number {
    return this.problem.status
  }

  get fieldErrors(): Record<string, string[]> {
    return this.problem.errors ?? {}
  }

  /** Primeira mensagem de erro de um campo, quando houver. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors[field]?.[0]
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ProblemDetails).status === 'number' &&
    typeof (value as ProblemDetails).title === 'string'
  )
}

/**
 * Converte uma resposta de erro em ApiError. Respostas que não são problem+json
 * (ex.: proxy fora do ar) viram um problem genérico com o status HTTP.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null

  try {
    body = await response.clone().json()
  } catch {
    // Corpo vazio ou não-JSON.
  }

  if (isProblemDetails(body)) {
    return new ApiError(body)
  }

  return new ApiError({
    type: 'about:blank',
    title: response.statusText || 'Erro de comunicação com o servidor.',
    status: response.status,
  })
}
